"use client";

import { useEffect, useRef, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { trackEvent } from "@/lib/analytics";
import type { AnalyticsEventName } from "@/lib/analytics/events";
import type { PublicEmployerJobPage } from "@/lib/marketplace/employer-job-pages";

const FALLBACK_ACCENT = "#1B2A4A";
const ANALYTICS_SURFACE = "jobs_marketplace";
const ANALYTICS_RETRY_DELAY_MS = 250;
const MAX_ANALYTICS_DELIVERY_ATTEMPTS = 12;

type AnalyticsRetryScheduler = (
  callback: () => void,
  delayMs: number,
) => void;

export interface DesignatedEmployerAnalyticsState {
  pendingImpressions: Set<string>;
  deliveredImpressions: Set<string>;
}

export function createDesignatedEmployerAnalyticsState(): DesignatedEmployerAnalyticsState {
  return {
    pendingImpressions: new Set<string>(),
    deliveredImpressions: new Set<string>(),
  };
}

const scheduleAnalyticsRetry: AnalyticsRetryScheduler = (callback, delayMs) => {
  setTimeout(callback, delayMs);
};

function deliverAnalyticsEvent(
  event: AnalyticsEventName,
  properties: Record<string, string>,
  schedule: AnalyticsRetryScheduler,
  onDelivered: () => void,
  onDropped: () => void,
  attempt = 1,
): void {
  if (trackEvent(event, properties)) {
    onDelivered();
    return;
  }
  if (attempt >= MAX_ANALYTICS_DELIVERY_ATTEMPTS) {
    onDropped();
    return;
  }
  schedule(
    () => deliverAnalyticsEvent(
      event,
      properties,
      schedule,
      onDelivered,
      onDropped,
      attempt + 1,
    ),
    ANALYTICS_RETRY_DELAY_MS,
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function safeAccentColour(value: string | null): string {
  return value && /^#[0-9a-f]{6}$/i.test(value) ? value : FALLBACK_ACCENT;
}

export function recordDesignatedEmployerImpressions(
  employers: readonly PublicEmployerJobPage[],
  state: DesignatedEmployerAnalyticsState,
  schedule: AnalyticsRetryScheduler = scheduleAnalyticsRetry,
): void {
  for (const employer of employers) {
    if (
      state.pendingImpressions.has(employer.companyId) ||
      state.deliveredImpressions.has(employer.companyId)
    ) {
      continue;
    }
    state.pendingImpressions.add(employer.companyId);
    deliverAnalyticsEvent(
      "designated_employer_card_impression",
      {
        company_id: employer.companyId,
        surface: ANALYTICS_SURFACE,
      },
      schedule,
      () => {
        state.pendingImpressions.delete(employer.companyId);
        state.deliveredImpressions.add(employer.companyId);
      },
      () => {
        state.pendingImpressions.delete(employer.companyId);
      },
    );
  }
}

export function recordDesignatedEmployerClick(
  companyId: string,
  schedule: AnalyticsRetryScheduler = scheduleAnalyticsRetry,
): void {
  deliverAnalyticsEvent(
    "designated_employer_card_clicked",
    {
      company_id: companyId,
      surface: ANALYTICS_SURFACE,
    },
    schedule,
    () => undefined,
    () => undefined,
  );
}

export function DesignatedEmployerModule({
  employers,
}: {
  employers: PublicEmployerJobPage[];
}): ReactNode {
  const analyticsState = useRef(createDesignatedEmployerAnalyticsState());

  useEffect(() => {
    recordDesignatedEmployerImpressions(employers, analyticsState.current);
  }, [employers]);

  if (employers.length === 0) return null;

  return (
    <section
      aria-labelledby="designated-employers-heading"
      className="mb-4"
    >
      <h2 id="designated-employers-heading" className="mb-2 font-heading text-sm font-bold text-navy">
        Jobs by employer
      </h2>

      <div className="flex flex-nowrap snap-x snap-mandatory gap-3 overflow-x-auto overscroll-x-contain pb-2">
        {employers.map((employer) => {
          const accentColour = safeAccentColour(employer.companyBrandColour);
          return (
            <Link
              key={employer.companyId}
              href={`/company/${employer.companySlug}/jobs`}
              onClick={() => recordDesignatedEmployerClick(employer.companyId)}
              className="group relative flex min-h-[44px] w-[calc(100%_-_24px)] max-w-[360px] shrink-0 snap-start items-center gap-3 overflow-hidden rounded-md border border-border-light bg-white p-3 text-left no-underline transition-colors hover:border-teal/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 sm:w-[320px]"
            >
              <span
                aria-hidden="true"
                className="absolute inset-y-0 left-0 w-1"
                style={{ backgroundColor: accentColour }}
              />
              {employer.companyLogoUrl ? (
                <Image
                  src={employer.companyLogoUrl}
                  alt=""
                  width={40}
                  height={40}
                  unoptimized
                  className="h-10 w-10 shrink-0 rounded-sm border border-border-light bg-white object-contain p-1"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm text-sm font-semibold text-white"
                  style={{ backgroundColor: accentColour }}
                >
                  {initials(employer.companyName)}
                </span>
              )}

              <span className="min-w-0 flex-1">
                <span className="block font-heading text-sm font-bold leading-5 text-navy">
                  {employer.companyName}
                </span>
                <span className="mt-1 block text-xs text-text-muted">
                  {employer.activeListingCount}{" "}
                  {employer.activeListingCount === 1 ? "active role" : "active roles"}
                </span>
              </span>
              <span aria-hidden="true" className="shrink-0 text-teal-dark">&rarr;</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
